import { useState, useEffect } from 'react';
import { Loader, Loader2, X, Users, Mail, UserPlus, Trash2, Shield, ShieldOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { User } from '../lib/types';
import { formatDateTime } from '../lib/text';

const UsersPage = () => {
  const { isAdmin, token, user: currentUser, authSettings, authFetch } = useAuth();
  const { showToast } = useToast();

  const [managedUsers, setManagedUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [newUser, setNewUser] = useState<{ email: string; full_name: string; roles: string[] }>({ email: '', full_name: '', roles: ['user'] });
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [updatingRolesId, setUpdatingRolesId] = useState<string | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin && token) fetchUsers();
  }, [isAdmin, token]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await authFetch('/users');
      if (!response.ok) throw new Error('Failed to fetch users');
      setManagedUsers(await response.json());
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingUser(true);
    setUsersError('');
    try {
      const response = await authFetch('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create user');
      }
      setNewUser({ email: '', full_name: '', roles: ['user'] });
      showToast('User invitation sent', 'success');
      fetchUsers();
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    setDeletingUserId(userId);
    try {
      const response = await authFetch(`/users/${userId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      setManagedUsers(managedUsers.filter(u => u.id !== userId));
      showToast('User deleted', 'success');
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleToggleAdmin = async (user: User) => {
    const userIsAdmin = user.roles.includes('administrator');
    const newRoles = userIsAdmin
      ? user.roles.filter(r => r !== 'administrator')
      : [...user.roles.filter(r => r !== 'administrator'), 'administrator'];
    if (!newRoles.includes('user')) newRoles.unshift('user');

    setUpdatingRolesId(user.id);
    try {
      const response = await authFetch(`/users/${user.id}/roles`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roles: newRoles }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update roles');
      }
      const updatedUser = await response.json();
      setManagedUsers(managedUsers.map(u => u.id === user.id ? updatedUser : u));
      showToast('User role updated', 'success');
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to update roles');
    } finally {
      setUpdatingRolesId(null);
    }
  };

  const handleResendInvite = async (userId: string) => {
    setResendingInviteId(userId);
    try {
      const response = await authFetch(`/users/${userId}/resend-invite`, {
        method: 'POST',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to resend invite');
      }
      showToast('Invitation email resent', 'success');
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to resend invite');
    } finally {
      setResendingInviteId(null);
    }
  };

  const toggleNewUserRole = (role: string) => {
    if (role === 'user') return;
    const currentRoles = newUser.roles;
    if (currentRoles.includes(role)) {
      setNewUser({ ...newUser, roles: currentRoles.filter(r => r !== role) });
    } else {
      setNewUser({ ...newUser, roles: [...currentRoles, role] });
    }
  };

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold">User Management</h3>
        <p className="text-sm text-coolslate mt-1">Manage user accounts and permissions</p>
      </div>

      <div className="space-y-6">
          <div className="bg-white border border-softgrey rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <UserPlus className="w-5 h-5 text-coolslate" />
              <p className="font-medium text-graphite">Invite User</p>
            </div>
            <form onSubmit={handleAddUser} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-graphite mb-1">Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-softgrey rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-graphite mb-1">Email Address</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-softgrey rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-graphite mb-1">Roles</label>
                <div className="flex flex-wrap gap-2">
                  {authSettings?.available_roles?.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleNewUserRole(role)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                        newUser.roles.includes(role)
                          ? role === 'administrator'
                            ? 'bg-brand-dark text-white'
                            : 'bg-brand text-white'
                          : 'bg-softgrey/50 text-coolslate hover:bg-softgrey'
                      }`}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </button>
                  )) || (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleNewUserRole('user')}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          newUser.roles.includes('user')
                            ? 'bg-brand text-white'
                            : 'bg-softgrey/50 text-coolslate hover:bg-softgrey'
                        }`}
                      >
                        User
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleNewUserRole('administrator')}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          newUser.roles.includes('administrator')
                            ? 'bg-brand-dark text-white'
                            : 'bg-softgrey/50 text-coolslate hover:bg-softgrey'
                        }`}
                      >
                        Administrator
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-coolslate">An invitation email will be sent to set their password.</p>
                <button
                  type="submit"
                  disabled={addingUser}
                  className="navaris-btn-solid"
                >
                  {addingUser ? (
                    <>
                      <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 shrink-0" />
                      Send Invite
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {usersError && (
            <div className="bg-state-error/10 border border-state-error/30 text-state-error px-4 py-3 rounded-lg flex items-center justify-between">
              <span>{usersError}</span>
              <button onClick={() => setUsersError('')} className="text-state-error hover:text-state-error">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {usersLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : (
            <div className="bg-white border border-softgrey rounded-lg overflow-x-auto">
              <table className="w-full min-w-175">
                <thead className="bg-softgrey/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-coolslate">User</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-coolslate">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-coolslate">Roles</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-coolslate">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-coolslate">Created</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-coolslate">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-softgrey">
                  {managedUsers.map((user) => {
                    const userIsAdmin = user.roles.includes('administrator');
                    const isPending = user.is_active !== 'true';
                    const isCurrentUser = user.id === currentUser?.id;
                    const initials = user.full_name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <tr key={user.id} className="hover:bg-white/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${userIsAdmin ? 'bg-brand-dark' : 'bg-brand'}`}>
                              {initials}
                            </div>
                            <div>
                              <p className="font-medium text-graphite">{user.full_name}</p>
                              {isCurrentUser && (
                                <span className="text-xs text-brand">(You)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-coolslate">{user.email}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {user.roles.map(role => (
                              <span
                                key={role}
                                className={`px-2 py-1 text-xs rounded-full ${
                                  role === 'administrator'
                                    ? 'bg-brand-dark/15 text-brand-dark'
                                    : 'bg-softgrey/50 text-graphite'
                                }`}
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {isPending ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-state-warning/15 text-state-warning">
                              Pending Invite
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-state-success/15 text-state-success">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-coolslate text-sm">
                          {formatDateTime(user.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {isPending && (
                              <button
                                onClick={() => handleResendInvite(user.id)}
                                disabled={resendingInviteId === user.id}
                                className="p-2 text-coolslate hover:text-brand hover:bg-brand/5 rounded-lg transition-colors disabled:opacity-50"
                                title="Resend invite email"
                              >
                                {resendingInviteId === user.id ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  <Mail className="w-5 h-5" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleAdmin(user)}
                              disabled={isCurrentUser || updatingRolesId === user.id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                userIsAdmin
                                  ? 'text-brand-dark hover:bg-brand-dark/5'
                                  : 'text-coolslate hover:text-brand-dark hover:bg-brand-dark/5'
                              }`}
                              title={userIsAdmin ? 'Remove admin role' : 'Make administrator'}
                            >
                              {updatingRolesId === user.id ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : userIsAdmin ? (
                                <Shield className="w-5 h-5" />
                              ) : (
                                <ShieldOff className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={isCurrentUser || deletingUserId === user.id}
                              className="p-2 text-coolslate hover:text-state-error hover:bg-state-error/5 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete user"
                            >
                              {deletingUserId === user.id ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <Trash2 className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {managedUsers.length === 0 && (
                <div className="text-center py-12 text-coolslate">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No users found</p>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default UsersPage;
